import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import { initSentry } from "@/lib/sentry";
import { flushClientDiagnostics } from "@/lib/clientDiagnostics";
import { flushAccountCreationRefusals } from "@/lib/accountCreationLog";
import { initStaleChunkReload } from "@/lib/staleChunkReload";
import { areUpdatesHeld, onUpdatesReleased } from "@/lib/updateHold";
import App from "./App.tsx";
import "./index.css";

// Error tracking first, so everything after it — service worker registration
// included — reports failures.
initSentry();

// Deliver any diagnostics and refused sign-ups a previous session queued but
// couldn't send (flaky-connection devices; see src/lib/clientDiagnostics.ts).
flushClientDiagnostics();
flushAccountCreationRefusals();

// Recover from lazy-route imports that 404 because the tab is running a build
// whose chunks a later deploy replaced. Armed before App renders so the very
// first route import is covered.
initStaleChunkReload();

declare global {
  interface Window {
    // Set once a new service worker has taken control but the page is still
    // running the old build. Read by RouteChangeUpdater (src/App.tsx), which
    // turns the next in-app navigation into a real page load.
    __swUpdatePending?: boolean;
  }
}

// Register service worker for push notifications and PWA support.
//
// This is the ONLY thing that updates the app. Nothing else may unregister the
// worker or delete caches on a version change: doing so races the handshake below
// and can strand the notice on screen with no reload behind it (iOS especially).
// Settings' "Clear Cache & Reload" stays as the deliberate manual escape hatch.
//
// A new worker activates only once the browser has *detected* it, and long-lived
// sessions — iOS home-screen PWAs above all — rarely re-check on their own, which
// strands users on an old build. So check at launch, shortly after, on an
// interval, and whenever the user returns to the app. Once detected, the worker
// activates itself (skipWaiting on install in src/sw.ts); the client's only job
// is reloading at a polite moment — when the app is hidden.
const SW_UPDATE_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

// Brief, self-contained notice about an update. Plain DOM (no React
// dependency) so it always renders, even mid-transition. Always auto-dismisses:
// updates apply when the app next goes hidden, so no reload is guaranteed to
// follow — a notice with no exit once sat on screen indefinitely saying
// "updating…" while the swap (correctly) waited for the user to look away.
function showUpdateNotice(message: string, autoHideMs: number): void {
  if (document.getElementById('sw-update-notice')) return;
  const el = document.createElement('div');
  el.id = 'sw-update-notice';
  el.setAttribute('role', 'status');
  el.textContent = message;
  el.style.cssText = [
    'position:fixed',
    'left:50%',
    'bottom:calc(env(safe-area-inset-bottom, 0px) + 20px)',
    'transform:translateX(-50%)',
    'max-width:min(92vw,420px)',
    'background:#2E7DB2',
    'color:#fff',
    'padding:13px 18px',
    'border-radius:12px',
    'font:600 14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
    'text-align:center',
    'box-shadow:0 8px 30px rgba(0,0,0,.28)',
    'z-index:2147483647',
    'opacity:0',
    'transition:opacity .25s ease',
  ].join(';');
  document.body.appendChild(el);
  requestAnimationFrame(() => { el.style.opacity = '1'; });
  window.setTimeout(() => {
    el.style.opacity = '0';
    window.setTimeout(() => el.remove(), 300);
  }, autoHideMs);
}

// A development build never registers a worker (devOptions is off), but a
// worker is tied to the ORIGIN, not the build: one installed by a production
// build once served on this port stays registered in the browser and keeps
// answering the dev server's module requests from its stale-while-revalidate
// cache. The symptom (2 Sep) was a card flipping between two versions on
// alternate reloads. Clear any such worker and its caches on every dev start.
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .then((results) => {
      if (results.some(Boolean)) console.info('[dev] unregistered a leftover service worker');
    })
    .catch(() => {});
  if ('caches' in window) {
    caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).catch(() => {});
  }
}

if ('serviceWorker' in navigator) {
  // The worker activates itself as soon as it installs (see src/sw.ts), so
  // updates land silently. The only client job is picking the moment to reload
  // into the new build — never under the user's fingers. Armed only when a
  // controller already exists, so the very first install never causes a reload.
  //
  // Reloading only while hidden works on desktop, where a backgrounded tab
  // keeps running and completes the reload. It does NOT work in an iOS PWA:
  // going hidden there means the process is frozen, so the queued reload never
  // runs, and iOS then restores the old page from a snapshot instead of
  // re-navigating — which is exactly why desktop updated on publish while the
  // iPhone sat on an old build. So also reload on the way back IN: the moment
  // the app returns to the foreground is before the user has touched anything,
  // and it is the only moment an iOS PWA is reliably awake to do it.
  let reloadingForNewWorker = false;
  // Set when a reload was wanted but a screen was holding updates off, so the
  // swap can happen the moment that work is finished instead of being lost.
  let reloadDeferred = false;
  const reloadOnce = () => {
    if (reloadingForNewWorker) return;
    // Never reload out from under someone filling in a form — that is typed
    // work living only in the page (src/lib/updateHold.ts).
    if (areUpdatesHeld()) {
      reloadDeferred = true;
      return;
    }
    reloadingForNewWorker = true;
    window.location.reload();
  };

  // Work is done: take the update that was waiting on it. Only while hidden or
  // on the next visible moment would be too clever here — the user has just
  // finished, and finishing is itself a natural break.
  onUpdatesReleased(() => {
    if (reloadDeferred) reloadOnce();
  });
  let newWorkerTookControl = false;
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      newWorkerTookControl = true;
      // Page navigation is another natural break — see swUpdatePending below.
      window.__swUpdatePending = true;
      if (document.visibilityState === 'hidden') reloadOnce();
    });
    document.addEventListener('visibilitychange', () => {
      if (!newWorkerTookControl) return;
      // Hidden: desktop path — reload now, while the tab can still run it.
      // Visible: iOS path — the freeze ate the hidden reload; do it on return.
      // If this handler is running on the way back in, any earlier reload
      // attempt provably never completed (a successful reload replaces the
      // document), so re-arm the guard rather than let a stale flag block
      // the retry. No loop risk: another eaten reload just retries on the
      // next return, and a completed one starts a fresh page.
      if (document.visibilityState === 'visible') reloadingForNewWorker = false;
      reloadOnce();
    });
    // Safari restoring a frozen page from bfcache fires pageshow, not always
    // visibilitychange. Same decision, different doorway — and a bfcache
    // restore likewise proves any prior reload attempt was eaten.
    window.addEventListener('pageshow', (event) => {
      if (newWorkerTookControl && (event as PageTransitionEvent).persisted) {
        reloadingForNewWorker = false;
        reloadOnce();
      }
    });
  }

  let swRegistration: ServiceWorkerRegistration | undefined;
  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      swRegistration = registration;

      // Stuck-state recovery. A worker already WAITING at launch means a prior
      // update handshake never finished (its timer died with the page, or iOS
      // suspended the app mid-swap) — the notice would otherwise reappear on
      // every launch while the app stays old forever. Finish the swap now:
      // activate the waiting worker directly, and if controllerchange doesn't
      // land (an iOS quirk), force one reload — capped per session so a broken
      // worker can never loop the app.
      if (registration.waiting) {
        showUpdateNotice('New version detected — updating to the latest version…', 8000);
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.setTimeout(() => {
          const key = 'sw-recovery-reloads';
          const attempts = Number(sessionStorage.getItem(key) || '0');
          if (attempts < 2) {
            sessionStorage.setItem(key, String(attempts + 1));
            reloadOnce();
          }
        }, 4000);
      }

      // Check for a new worker NOW and again shortly after launch. The
      // browser's own navigation-time check is lazy on iOS and can silently
      // fail on a cold cellular radio — without these, a missed launch check
      // left the app blind for the full 30-minute interval even while the
      // user sat waiting in the foreground.
      const checkForUpdate = () => { registration.update().catch(() => {}); };
      checkForUpdate();
      window.setTimeout(checkForUpdate, 30 * 1000);
      window.setTimeout(checkForUpdate, 2 * 60 * 1000);
      window.addEventListener('online', checkForUpdate);
      setInterval(checkForUpdate, SW_UPDATE_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });
    },
    // A new version was detected. The worker self-activates on install (see
    // src/sw.ts) and the reload is hidden-gated above, so there is nothing to
    // trigger here — updateSW(true) would race a worker that often takes
    // control before its 'controlling' listener attaches, reloading nothing
    // while an "updating…" notice sat on screen forever. Just promote a worker
    // that somehow got stuck waiting, and tell the user the truth: the update
    // applies the next time they come back.
    onNeedRefresh() {
      swRegistration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
      showUpdateNotice('Update downloaded — it will apply the next time you open the app.', 6000);
    },
  });
}

createRoot(document.getElementById("root")!).render(<App />);
